import { useStore } from '@tanstack/react-form'
import { z } from 'zod'

import { dateOfBirthSchema, formatCurrency, formatInterval } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { Button } from '@/frontend/components/ui/button'
import { useCreatePatient } from '@/frontend/hooks/api/patients/use-create-patient'
import { useListPlans } from '@/frontend/hooks/api/plans/use-list-plans'

const addPatientSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  species: z.string().min(1, 'Species is required'),
  sex: z.enum(['male', 'female'], { message: 'Sex is required' }),
  breed: z.string().min(1, 'Breed is required'),
  dateOfBirth: z.string().min(1, 'Date of birth is required').pipe(dateOfBirthSchema),
  color: z.string().trim().nullable(),
  isNeutered: z.boolean().nullable(),
  microchipNumber: z.string().nullable(),
  priceId: z.string().nullable(),
})

type AddPatientFormValues = z.infer<typeof addPatientSchema>

const SPECIES_OPTIONS = [
  { label: 'Dog', value: 'Dog' },
  { label: 'Cat', value: 'Cat' },
]

const SEX_OPTIONS = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
]

const NEUTERED_OPTIONS = [
  { label: 'Unknown', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
]

/** Form for adding a new patient to a member, with optional Stripe subscription. */
export function CreatePatientForm({
  memberId,
  onSuccess,
  onCancel,
}: {
  memberId: string
  onSuccess: () => void
  onCancel: () => void
}) {
  const { mutateAsync: createPatient } = useCreatePatient(memberId)
  const { data: plansData } = useListPlans()

  const priceOptions =
    plansData?.plans.map((plan) => ({
      label: `${formatInterval(plan.interval, plan.intervalCount)} · ${formatCurrency(plan.amount, plan.currency)}`,
      value: plan.priceId,
    })) ?? []
  priceOptions.unshift({ label: 'No subscription', value: '' })

  const form = useAppForm({
    defaultValues: {
      color: null,
      isNeutered: null,
      microchipNumber: null,
      priceId: null,
    } as AddPatientFormValues,
    validators: { onSubmit: addPatientSchema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await createPatient({
          name: value.name,
          species: value.species,
          sex: value.sex as 'male' | 'female',
          breed: value.breed,
          dateOfBirth: value.dateOfBirth,
          color: value.color || null,
          isNeutered: value.isNeutered,
          microchipNumber: value.microchipNumber,
          // 'No subscription' option uses '' as its value (Base UI Select can't bind null);
          // coerce to null here so the backend's z.string().min(1).nullable() accepts it.
          priceId: value.priceId || null,
        })
        onSuccess()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to add patient',
            fields: {},
          },
        })
      }
    },
  })

  // Drive the neutered/spayed label from the live sex value
  const sex = useStore(form.store, (s) => s.values.sex)
  const neuteredLabel = sex === 'female' ? 'Spayed' : 'Neutered'

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          <form.AppField name="name">
            {(field) => <field.InputField label="Name" placeholder="Buddy" required />}
          </form.AppField>

          <div className="flex gap-3">
            <form.AppField name="species">
              {(field) => (
                <field.SelectField
                  label="Species"
                  placeholder="Select"
                  items={SPECIES_OPTIONS}
                  layoutClassName="flex-1"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="breed">
              {(field) => (
                <field.InputField
                  label="Breed"
                  placeholder="Labrador"
                  layoutClassName="flex-1"
                  required
                />
              )}
            </form.AppField>
          </div>

          <div className="flex gap-3">
            <form.AppField name="sex">
              {(field) => (
                <field.SelectField
                  label="Sex"
                  placeholder="Select"
                  items={SEX_OPTIONS}
                  layoutClassName="flex-1"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="isNeutered">
              {(field) => (
                <field.SelectField
                  label={neuteredLabel}
                  placeholder="Unknown"
                  items={NEUTERED_OPTIONS}
                  layoutClassName="flex-1"
                />
              )}
            </form.AppField>
          </div>

          <div className="flex gap-3">
            <form.AppField name="dateOfBirth">
              {(field) => (
                <field.InputField
                  label="Date of birth"
                  type="date"
                  layoutClassName="flex-1"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="microchipNumber">
              {(field) => (
                <field.InputField
                  label="Microchip number"
                  placeholder="982000123456789"
                  layoutClassName="flex-1"
                />
              )}
            </form.AppField>
          </div>

          <form.AppField name="color">
            {(field) => <field.InputField label="Colour" placeholder="Black & tan" />}
          </form.AppField>

          <form.AppField name="priceId">
            {(field) => (
              <field.SelectField
                label="Subscription plan"
                placeholder="No subscription"
                items={priceOptions}
              />
            )}
          </form.AppField>
        </div>

        <div className="pt-3 flex items-center gap-x-2 justify-end">
          <Button variant="outline" size="lg" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" size="lg" disabled={isSubmitting}>
                {isSubmitting ? 'Adding...' : 'Add Patient'}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form.AppForm>
    </form>
  )
}
